import {VNode} from 'snabbdom';

import {
  ChartType,
  COUNT_USERS_ONCE_TYPE,
  CountUsersOnceState,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  PropertyType,
  ReportName,
  ResourceType,
} from 'common/report/constants';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {DisplayContext, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {FilterOperator} from 'common/widgets/property-filter-menu/models/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import type {ChartPlotStyle, ShowClauseFormula} from 'common/report/insights/util/types';
import {RollingWindowSize} from 'common/util';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {Segmentation} from 'common/types/reports/query';
import {BookmarkFunnelExclusionStep} from 'common/report/funnels/models/types';
import {ConversionWindowUnit, FunnelOrder, FunnelsCountType} from 'common/report/funnels/models/constants';
import type {RetentionAlignmentType, RetentionType} from 'common/report/retention/util/types';
import {SectionItem} from 'common/components/items-menu/types';
import {ButtonTheme} from 'common/components/button/constants';

import {EventItem} from '../query-block/events-menu/types';
import {PropertyCombineMode} from '../query-block/properties-menu/types';
import {InlineCohortSegment, SegmentValue} from '../query-block/segment-menu/types';
import {PropertySectionItem} from '../query-block/properties-menu/item-menu-types';
import {SelectorTypeAggregationLabel} from '../types/shared-types';
import {MetricType} from '../query-block/constants';

export type IncludeCustomProperties = `none` | `persisted-only` | `persisted-non-behavioral-only` | `all`;

export interface QueryEntrySectionAttrs {
  'add-entry-label': string;
  'add-entry-button-label': string;
  'add-entry-button-tooltip-text': string;
  'add-entry-menu-width': number;
  'allow-cohort-filter': boolean;
  'autoactivate-placeholders': boolean;
  'button-theme': ButtonTheme;
  'can-rename-step': boolean;
  'chart-type': ChartType;
  'enable-enhanced-list-filters': boolean;
  'group-event-behavior': GroupEventBehaviors;
  'data-group-id': string;
  'data-groups-enabled': boolean;
  'date-range': string;
  'date-unit': RollingWindowSize;
  'disable-map-to-lookup-table': boolean;
  'disabled-items': Obj;
  'property-combine-mode': PropertyCombineMode;
  'disable-add-event-filters': boolean;
  'disable-clone-entry': boolean;
  'disable-delete-entry': boolean;
  'disable-first-time-event-filters': boolean;
  'enable-workspace-filter-restrictions': boolean;
  'disable-add-grouped-event-reason': string;
  disabled: boolean;
  'disable-add-entry': boolean;
  'disable-add-entry-reason': string;
  'disable-profile-history': boolean;
  'entry-type': EntryType;
  'event-aggregator-functions': MATH_TYPE[];
  'row-label-type-for-exclusion-steps': RowLabelType;
  'property-aggregator-functions': MATH_TYPE[];
  entries: Array<MetricEntry | QuerySuperEntry>;
  'min-entries': number;
  'empty-placeholder': QuerySuperEntry;
  'exclude-autotrack-events': boolean;
  'exclude-custom-events': boolean;
  'exclude-dimension-properties': boolean;
  'include-custom-properties': IncludeCustomProperties;
  'is-blocked-people': boolean;
  'is-referenced-metrics': boolean;
  autofocus: boolean;
  'max-data-history-days': number;
  'metrics-mode': boolean;
  'behavior-mode': boolean;
  'num-exclusion-steps': number;
  'plot-style': ChartPlotStyle;
  'project-has-cohorts': boolean;
  'project-has-people': boolean;
  'project-id': number;
  'project-is-free-plan': boolean;
  'user-id': number;
  'row-draggable': boolean;
  'row-drop-disabled': boolean;
  'undraggable-rows': number[];
  readonly: boolean;
  'restrict-top-events-only': boolean;
  'row-types': RowTypeOption[];
  'row-index-toggleable': boolean;
  'row-index-toggle-icon': string;
  'row-index': number;
  'row-label-prefix': string;
  'row-label-prefix-icon': string;
  'row-label-type': RowLabelType;
  'row-limit': number;
  'show-all-events': boolean;
  'show-any-event': boolean;
  'hide-edit-controls': boolean;
  'show-sessions': boolean;
  'show-top-events': boolean;
  'step-count': number;
  'show-special-dataset-property': boolean;
  'support-b2b-cohorts': boolean;
  'inline-cohorts-override': Obj;
  'time-display-context': DisplayContext;
  'track-name': string;
  'report-name': ReportName;
  'track-properties': UnknownObj;
  'workspace-id': number;
  'project-as-of-date': string;
  'event-names': string[];
  'other-group-by-options': Array<PropertySectionItem<string>>;
  'other-filter-options': Array<PropertySectionItem<string>>;
  'other-aggregation-options': Array<PropertySectionItem<string>>;
  'hide-events': boolean;
  'scd-poc-enabled': boolean;
  'can-customize-numeric-buckets': boolean;
  'can-use-multi-attribution': boolean;
  'count-users-once-state': CountUsersOnceState;
  /** when rendering the section as a styled container, includes a title in the header area */
  'section-title': string;
  'enable-sticky-header': boolean;
  'enable-hide-entry': boolean;
  'enable-event-icon': boolean;
}

export interface QueryEntrySectionParams {
  /** For list of objects, this is the parent list segmentation. */
  listProperty?: Segmentation;
  /** Disables certain typecasting options for rows. */
  disabledTypecastTypes?: PropertyType[];
  /** Used by the old QB to render row actions with the first list item filter. can be removed when old QB is deprecated. */
  rowActions?: VNode;
}

export enum SelectorTypeDefinitions {
  Behavior = `behavior`,
  EventLaunch = `event_launch`,
  FlowsEvent = `flows_event`,
  GroupedEvent = `grouped_event`,
  Filter = `filter`,
  Segment = `segment`,
  CohortGroup = `cohort_group`,
  PropertyExpansion = `property_expansion`,
}

/** TODO(pablo): remove union with ResourceType and flatten this list */
export type SelectorType = `metric-entry` | MetricType | ResourceType | SelectorTypeDefinitions;

export type RowTypeComplexOption = {
  value: SelectorType;
  placeholderEntry?: Partial<QuerySuperEntry>;
  includeDateRange?: boolean;
  disabledReason?: string;
};

/** A RowType represents what kind of entries can be added to the list */
export type RowTypeOption = SelectorType | RowTypeComplexOption;

/** TODO(pablo): remove type based helpers once we can remove `event`
 * for representing all value selections in an entry
 */
export function isEventBasedEntry(
  entry: Entry,
): entry is MetricEntry | QueryEventEntry | QueryBehaviorEntry | QueryEventLaunch | FlowsQueryEventEntry {
  if (isMetricEntry(entry)) {
    return [MetricType.CustomEvent, MetricType.Event, MetricType.Funnel, MetricType.Retention].includes(
      entry.behavior.type,
    );
  }
  const type: SelectorType = entry.type;
  const propertyTypes = [
    ResourceType.Event,
    SelectorTypeDefinitions.Behavior,
    SelectorTypeDefinitions.EventLaunch,
    SelectorTypeDefinitions.FlowsEvent,
  ] as SelectorType[];

  return propertyTypes.includes(type || ResourceType.Event);
}

export function isUserBasedEntry(entry: Entry): entry is QueryUserEntry | MetricEntry {
  if (isMetricEntry(entry)) {
    return entry.behavior.type === MetricType.People;
  }
  const propertyTypes = [ResourceType.User] as SelectorType[];
  return propertyTypes.includes(entry.type);
}

export function isPropertySelectorType(type: SelectorType): type is QueryPropertyEntry['type'] {
  const propertyTypes = [
    SelectorTypeDefinitions.Filter,
    SelectorTypeDefinitions.Segment,
    SelectorTypeDefinitions.PropertyExpansion,
  ] as SelectorType[];
  return propertyTypes.includes(type);
}

export enum FiltersOperator {
  And = `and`,
  Or = `or`,
  OrAll = `or_all`,
  AndAny = `and_any`,
  Then = `then`, // [shawn] Special-case for support of funnels queries within the cohorts-query-builder
}

export const FiltersOperatorDisplayLabels: {[x in FiltersOperator]?: string} = {
  [FiltersOperator.And]: `and`,
  [FiltersOperator.AndAny]: `and any`,
  [FiltersOperator.Or]: `or`,
  [FiltersOperator.OrAll]: `or all`,
  [FiltersOperator.Then]: `then`,
};

export interface SavedCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  renamed?: string;
  // "value" is the cohort's id
  value: number;
  data_group_id?: string;
  negated?: boolean;
}

export interface SpecialCohortItem extends Omit<SavedCohortItem, 'value'> {
  // string for pre-defined cohorts ($all_users)
  value: string;
}

interface InlineCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  value: InlineCohortSegment;
  negated?: boolean;
}
export type CohortItem = SavedCohortItem | InlineCohortItem | SpecialCohortItem;

export function isInlineCohortItem(item: CohortItem): item is InlineCohortItem {
  return item.value && (item as InlineCohortItem).value.cohort !== undefined;
}

export function isSavedCohortItem(item: CohortItem): item is SavedCohortItem {
  return item.value && typeof (item as SavedCohortItem).value === `number`;
}

export interface BaseEntry {
  dataGroupId?: string | null;

  /** Set of filter clauses to be applied to the event */
  filters?: PropertyFilter[];

  /** Set of behavioral filters */
  behavioralFilters?: PropertyFilter[];
  behavioralFiltersOperator?: FiltersOperator;

  /** Boolean operator to join filter clauses */
  filtersOperator?: FiltersOperator;

  /** Position of the event if this event specifies exclusion */
  excludeRange?: UnknownObj | {from: number; to: number};

  /** Specifies if exclusion event range will include all collection events */
  excludeAll?: boolean;

  /** Numeric property applied on event */
  property?: PropertySectionItem;

  /** Specific mathematical aggregation performed on event and its properties */
  aggregationOperator?: MATH_TYPE;

  /** Specific mathematical aggregation performed on a per-user basis */
  aggregationOperatorPerUser?: MATH_TYPE;

  rolling?: {rollingWindowSize: number};

  cumulative?: boolean;

  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;

  /** if set, the event index label is displayed in toggled state */
  entryIndexLabelToggle?: boolean;

  /** if set, the event value cannot be updated */
  readOnlyEvent?: boolean;

  /** for funnels and flows */
  forward?: number;
  reverse?: number;

  /** for internal use, stores committed values for an entry in stage mode */
  persistedEntry?: QuerySuperEntry;

  /** For internal use, the unique key of this entry row for use with snabbdom */
  entryKey?: number;

  /** internal non-serializable prefix label for the main entry selector */
  prefixLabel?: string;

  /** when set, this entry will be omitted from results */
  isHidden?: boolean;

  /** if true, expands the entry to displaky its definition (i.e. metrics in a formula, behavior in a metric) */
  isExpanded?: boolean;
}

// TODO: Rename event -> item or cohort/event/filter/segment
export interface QueryEventEntry extends BaseEntry {
  type: ResourceType.Event;
  event: EventItem;
  dateRange?: Partial<DateRange>;
  /** Custom bucketing for HISTOGRAM aggregationOperator  */
  customBucket?: Bucket;
  multiAttribution?: MultiAttribution;
}

export function isEventEntry(entry: Entry): entry is QueryEventEntry | MetricEntry {
  if (isMetricEntry(entry)) {
    return entry.behavior.type === MetricType.Event;
  }
  return entry.type === ResourceType.Event;
}

export type FormulaItem = ShowClauseFormula & {
  error?: string;
};

export interface QueryFormulaEntry extends BaseEntry {
  type: ResourceType.Formula;
  event: FormulaItem;
  multiAttribution?: MultiAttribution;
}

export function isFormulaEntry(entry: Entry): entry is QueryFormulaEntry {
  // TODO(CC): Create a new formula entry whose basis is MetricEntry
  if (isMetricEntry(entry)) {
    return false;
  }
  return entry && entry.type === ResourceType.Formula;
}

export function isInlineFormulaEntry(entry: Entry): boolean {
  return isFormulaEntry(entry) && !entry.event.id;
}

export interface FlowsQueryEventEntry extends BaseEntry {
  type: SelectorTypeDefinitions.FlowsEvent;
  event: EventItem;
  funnelStep?: boolean;
}

interface QueryUserEntry extends BaseEntry {
  type: ResourceType.User;
  event: EventItem;
}

export interface QueryCohortEntry extends BaseEntry {
  type: ResourceType.Cohort;
  event: CohortItem;
}

export function isCohortEntry(entry: Entry): entry is QueryCohortEntry | MetricEntry {
  if (isMetricEntry(entry)) {
    return entry.behavior.type === MetricType.Cohort;
  }
  return entry.type === ResourceType.Cohort;
}

export interface CohortGroupEntry extends BaseEntry {
  groupingOperator?: FiltersOperator.And | FiltersOperator.Or;
  type: SelectorTypeDefinitions.CohortGroup;
  event: CohortItem;
}

export interface QueryBehaviorEntry extends BaseEntry {
  /** @todo [pablo] query-entry-section does not have great typing and support for `Behavior` entries so we pass it as events in some places */
  type: SelectorTypeDefinitions.Behavior | ResourceType.Event;
  event: EventItem;
  dateRange?: Partial<DateRange>;
  entryFilter?: Partial<PropertyFilter>;
  aggregationOperator?: MATH_TYPE;
  filtersOperator?: FiltersOperator;
}

export interface QueryEventLaunch extends BaseEntry {
  type: SelectorTypeDefinitions.EventLaunch;
  event: EventItem;
  dateRange?: Partial<DateRange>;
}

/**
 * An entry which contains multiple child entries.
 *
 * TODO (later): shouldn't extend BaseEntry, but that would require more
 * refactoring of query-entry-section.
 */
export interface QueryGroupedEntry extends BaseEntry {
  type: SelectorTypeDefinitions.GroupedEvent;
  event: QueryEventEntry[];
}

export function isGroupedEntry(entry: Entry): entry is QueryGroupedEntry {
  if (isMetricEntry(entry)) {
    return false;
  }
  return entry.type === SelectorTypeDefinitions.GroupedEvent && Array.isArray(entry.event);
}

export interface OperatorOverride {
  value: FilterOperator;
  label?: string;
}

export interface QueryFilterEntry extends BaseEntry {
  clearable?: boolean; // TODO remove once we remove customize segments v1
  filterOnly?: boolean;
  hideListQuantifier?: boolean;
  filtersOperator?: FiltersOperator | null;
  type: SelectorTypeDefinitions.Filter;
  event: Partial<PropertyFilter>;
  quickDelete?: boolean;
  quickRename?: boolean;
  operatorOverrides?: OperatorOverride[] | null;
  suppressTracking?: boolean;
}

export function isFilterEntry(entry: QuerySuperEntry): entry is QueryFilterEntry {
  return entry && entry.type === SelectorTypeDefinitions.Filter;
}

export interface QuerySegmentEntry extends BaseEntry {
  type: SelectorTypeDefinitions.Segment;
  event: SegmentValue;
  customBucket?: Bucket;
}

export function isSegmentEntry(entry: Entry): entry is QuerySegmentEntry {
  if (isMetricEntry(entry)) {
    return false;
  }
  return entry.type === SelectorTypeDefinitions.Segment;
}

/** Union of entry types which may contain a customBucket. */
export type QueryBucketableEntry = QueryEventEntry | QuerySegmentEntry;

/**
 * QB-internal representation of Filter and Breakdown entry types.
 *
 * This is distinct from event/user property aggregations as a QueryMetricEntry.
 */
export type QueryPropertyEntry = QueryFilterEntry | QuerySegmentEntry;
export function isPropertyEntry(entry: Entry): entry is QueryPropertyEntry {
  if (isMetricEntry(entry)) {
    return false;
  }
  return isPropertySelectorType(entry.type);
}

export function isObjectEntry(entry: QuerySuperEntry) {
  return (entry as QueryFilterEntry)?.event?.joinPropertyType === PropertyType.Object;
}

/** A single entry representing one cohort, event, property, etc. */
export type QueryMetricEntry =
  | QueryEventEntry
  | QueryFormulaEntry
  | FlowsQueryEventEntry
  | QueryUserEntry
  | QueryCohortEntry
  | QueryBehaviorEntry
  | QueryEventLaunch;

/** All entry types that can be passed to the query builder. Safe for app code to depend on. */
export type QueryEntry = QueryMetricEntry | QueryGroupedEntry | CohortGroupEntry;

/**
 * Query builder internal: all possible types of query entry. App code shouldn't depend on this.
 */
export type QuerySuperEntry = QueryEntry | QueryMetricEntry | QueryPropertyEntry | QueryGroupedEntry;

export type EntryItem = QuerySuperEntry['event'];

export enum InsightsCountType {
  UniqueUsers = `Unique Users`,
  Total = `Total`,
  TotalSessions = `Total Sessions`,
  FrequencyPerUser = `Frequency per User`,
  PropertyValues = `Aggregate Property`,
  PropertyValuePerUser = `Aggregate Property per User`,
}

export enum RetentionCountType {
  Unique = `Unique`,
  RetentionRate = `Retention Rate`,
}

export enum CalculationTypes {
  Rolling = `rolling`,
  Cumulative = `cumulative`,
}
export interface Calculation {
  calculationType: CalculationTypes | null;
  rollingWindowSize?: number;
}

export type AdvancedMenuValue = Calculation | RangeSelection | number;

export type CountType = InsightsCountType | RetentionCountType | FunnelsCountType;

export interface CountBy<AdvancedMenuValueType extends AdvancedMenuValue = AdvancedMenuValue> {
  countType: CountType;
  aggregationOperator?: MATH_TYPE;
  aggregationOperatorPerUser?: MATH_TYPE;
  property?: PropertySectionItem;
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;
  advancedMenuValue?: AdvancedMenuValueType;
}

export enum Action {
  Add = `add`,
  AddFilter = `addFilter`,
  ApplyProperty = `applyProperty`,
  Clone = `clone`,
  Drag = `drag`,
  Edit = `edit`,
  Group = `group`,
  Ungroup = `ungroup`,
  EditFilter = `editFilter`,
  Remove = `remove`,
  RemoveFilter = `removeFilter`,
  Select = `select`,
  SelectDateRange = `selectDateRange`,
  EditFlowSteps = `editFlowSteps`,
  ToggleValueComparison = `toggleValueComparison`,
}

export enum RowLabelType {
  None = `none`,
  Alpha = `alpha`,
  Numeric = `numeric`,
}

export enum EntryType {
  Event = `event`,
  ExclusionEvent = `exclusion-event`,
}

export const SelectorTypesLabels: {[key in SelectorType]?: string} = {
  // event based types
  [ResourceType.Event]: `event`,
  [SelectorTypeDefinitions.FlowsEvent]: `event`,
  [SelectorTypeDefinitions.Behavior]: `behavior`,
  [SelectorTypeDefinitions.EventLaunch]: `event`,
  [SelectorTypeDefinitions.GroupedEvent]: `event`,
  [ResourceType.Cohort]: `cohort`,
  [ResourceType.User]: `user profile`,
};

const EMPTY_EVENT: EventItem = Object.freeze({
  id: null,
  custom: false,
  label: ``,
  value: null,
});

export const EMPTY_EVENT_ENTRY = Object.freeze({
  aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  event: EMPTY_EVENT,
  filters: [],
  filtersOperator: FiltersOperator.And,
  property: null,
  type: ResourceType.Event,
}) as QueryEventEntry;

export const EMPTY_EXCLUSION_EVENT_ENTRY: QueryEventEntry = Object.freeze({
  ...EMPTY_EVENT_ENTRY,
  excludeAll: true,
  excludeRange: {},
});

export type SelectorTypeAggregation = {
  /** specifies if the aggregation acts on a property */
  forProperty?: boolean;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;

  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;

  label: SelectorTypeAggregationLabel;
  description?: SelectorTypeAggregationLabel;
  subLabel?: SelectorTypeAggregationLabel;
  shortLabel?: SelectorTypeAggregationLabel;

  /** defines aggregations to be displayed in a nested menu */
  subSections?: SelectorTypeAggregation[];

  /** 2nd level aggregation definitions for a 1st pass aggregation, e.g. distribution on a Total per User by Distribution query */
  subAggregations?: SelectorTypeAggregation[];
};

/** When using Add grouped event, how to add the new row. */
export enum GroupEventBehaviors {
  /** Grouped events are disabled. `Add grouped event` is not allowed. */
  None = `none`,
  /** Adding a grouped event should append it to the current entries list. */
  Append = `append`,
  /** Adding a grouped event should create a new group at the current entry. */
  Nest = `nest`,
}

/** When a row-label-prefix is passed, how to display it and handle interactions. */
export enum RowPrefixBehaviors {
  /** Don't show a row prefix at all. */
  None = `none`,
  /** Show the prefix on every row. */
  EveryRow = `every`,
  /** Show the prefix only on the first row. */
  FirstOnly = `first`,
}

/** Identifier for a possibly nested entry. Ex: [0,2] = first entry, third child. */
export type RowAddress = [number] | [number, number];

export enum EntryPart {
  Aggregator = `aggregator`,
  FirstTimeFilters = `firstTimeFilters`,
  BehavioralFrequencyFilters = `behavioralFrequencyFilters`,
  CustomBucketing = `customBucketing`,
  MetricName = `metricName`,
  MultiAttribution = `multiAttribution`,
  MultiAttributionBehaviorFilterOperator = `multiAttributionBehaviorFilterOperator`,
  MultiAttributionBehaviorFilterValue = `multiAttributionBehaviorFilterValue`,
  MultiAttributionLookback = `multiAttributionLookback`,
  Filters = `filters`,
  NumericProperty = `numericProperty`,
  SubAggregator = `subAggregator`,
  Calculation = `calculation`,
  CountUsersOnce = `countUsersOnce`,
  FilterValue = `filterValue`,
  EntryFilterValue = `entryFilterValue`,
  Value = `value`,
  /** for list of objects breakdown */
  SubValue = `subValue`,
}

export type SectionOpenEntry = {index: number; part: EntryPart} | EmptyObj;

export type Entry = QuerySuperEntry | MetricEntry;

export type EntrySectionUpdateEvent<EntryType = Entry> = CustomEvent<{
  entries: EntryType[];
  updatedEntry: EntryType;
  updatedEntryIndex: number;
  queryReason?: QueryReason;
  requery: boolean;
}>;

export type UpdateStatus = {
  dispatchedUpdate: boolean;
};

export enum DROP_POSITION {
  ABOVE,
  BELOW,
  ON,
  HEADER,
}

export interface MetricEntryMeasurement {
  /** Numeric property applied on event */
  property?: PropertySectionItem;
  /** Specific mathematical aggregation performed on event and its properties */
  aggregationOperator?: MATH_TYPE;
  /** Specific mathematical aggregation performed on a per-user basis */
  aggregationOperatorPerUser?: MATH_TYPE;
  rolling?: {rollingWindowSize: number};
  cumulative?: boolean;
  multiAttribution?: MultiAttribution;
  // funnel specific
  /** controls conversion step, if it's null it's "all step", other wise it's step {eventIndex} -> step {eventIndex + 1} */
  // TODO: change to funnelStepIndex
  stepIndex?: number;
  // funnel specific
  retentionBucketIndex?: number;
  retentionBuckets?: Array<SectionItem<number>>;
}

export interface MetricEntryBehavior {
  type: MetricType;
  // depends on the type, id could be any of the
  // 1. custom-event
  // 2. funnel
  // 3. retention
  // 4. cohort
  id?: number | string;

  // b2b data group
  dataGroupId?: string | null;
  // for `event` type, name is the "Event Name"
  // for `profile` it's always "$all_people"
  // for `saved-cohort`, it holds name of the cohort
  // for `inline-cohort`, it holds the name user input
  // for `inline-custom-event`, it holds the name user input
  // for `inline-funnels`, it holds the name user input
  name: string;
  // only appliable for "event" | "saved-cohort" | "profile"
  renamed?: string;

  // filters applied on the "behavior block"
  filters?: PropertyFilter[];
  filtersOperator?: FiltersOperator;
  filtersDeterminer?: 'all' | 'any';

  //========================
  // generic nestable field
  //========================
  // for inline-custom-event, this holds custom event definition
  // for inline-funnels, this holds funnel steps
  // for inline-retention, this holds exactly 2 entries - born & event
  // special case: for funnels event comparision feature, this will be nested 2 levels
  behaviors?: MetricEntryBehavior[];

  //========================
  // inline-cohort specific
  //========================
  raw_cohort?: TODO; // RawCohort

  //========================
  // insight specific
  //========================
  // only appliable when county type is histogram
  customBucket?: Bucket;

  //========================
  // funnels specific
  //========================
  funnelOrder?: FunnelOrder;
  // exclusion could only contain `event` | `saved-custom-event`
  exclusions?: BookmarkFunnelExclusionStep[];
  // "holding property constant" feature, selets a property
  aggregateBy?: Segmentation[];
  conversionWindowDuration?: number;
  conversionWindowUnit?: ConversionWindowUnit;
  /** if set, the event index label is displayed in toggled state */
  entryIndexLabelToggle?: boolean;

  //========================
  // retention specific
  //========================
  retentionType?: RetentionType;
  // calendar day
  retentionAlignmentType?: RetentionAlignmentType;
  // day | week | year
  retentionUnit?: TimeUnit;
  // on or after | on Each day
  retentionUnbounded?: boolean;
  // custom retention range
  retentionCustomBucketSizes?: number[];
}

export interface MetricEntry {
  dataGroupId?: string | null;
  type: `metric-entry`;
  //======================
  // behavior
  //======================
  behavior: MetricEntryBehavior;
  //=======================
  // measurement
  //=======================
  measurement: MetricEntryMeasurement;
  //========================
  // UI specific fields for metric
  //========================
  // rest of the UI state could just be added as needed.
  // here just list out some of the existing UI state in type def.
  /** if set, the event value cannot be updated */
  readOnlyEvent?: boolean;
  /** for internal use, stores committed values for an entry in stage mode */
  persistedEntry?: MetricEntry;
  /** For internal use, the unique key of this entry row for use with snabbdom */
  entryKey?: number;
  /** internal non-serializable prefix label for the main entry selector */
  prefixLabel?: string;
  /** if true, expands the metric entry that renders metric behavior section */
  isExpanded?: boolean;
  /** when set, this entry will be omitted from results */
  isHidden?: boolean;
}

export function isMetricEntry(entry: Entry): entry is MetricEntry {
  return entry.type === `metric-entry`;
}

export function isQuerySuperEntry(entry: Entry): entry is QuerySuperEntry {
  return Object.prototype.hasOwnProperty.call(entry, `event`);
}
